import { Component, Input, OnInit, input, InputSignal } from '@angular/core';
import { ConnectionService } from '@services/connection-service';
import { Router } from '@angular/router';
import { EventLoggerService } from '@services/event-logger-service';
import { AppConfig } from '../../../../app.config';
import { CrossSellRegimenService } from '@services/cross-sell-regimen-service/cross-sell-regimen.service';
import { AllocatedRegimen } from 'client/app/app-constant-types';
import { CrossSellRegimenExperiment } from '@services/experiment/experiment.service.types';

@Component({
  selector: 'cross-regimen-banner-v2',
  templateUrl: './cross-regimen-banner-v2.html',
  standalone: true,
  imports: []
})
export class CrossRegimenBannerV2Component implements OnInit {
  @Input() latestActiveRegimen: AllocatedRegimen;
  @Input() user: any;
  experiments: any[];
  faceMainConcern: string;
  requiredRegimenClass: string;
  discountAmount: number;
  PREGNANCY_TAGS: string[] = ['PREGNANT', 'BREASTFEEDING', 'NEW_MOM'];
  crossSellRegimenExperiment: InputSignal<CrossSellRegimenExperiment> = input<CrossSellRegimenExperiment>()

  constructor(
    public conn: ConnectionService,
    public appConfig: AppConfig,
    public router: Router,
    public eventLogger: EventLoggerService,
  ) {}

  async ngOnInit(): Promise<void> {
    this.getFaceMainConcern();
    this.requiredRegimenClass = this.getOppositeClass(this.latestActiveRegimen?.class);
    this.extractDiscount(this.crossSellRegimenExperiment()?.variant);
  }

  private getFaceMainConcern(): void {
    if (this.latestActiveRegimen?.class === this.appConfig.Shared.Regimen.Class.FACE
      && this.latestActiveRegimen?.displayConcern) {
      this.faceMainConcern = this.latestActiveRegimen?.displayConcern.name;
    }
  }

  private getOppositeClass(current: string): string {
    const { FACE, HAIR } = this.appConfig.Shared.Regimen.Class;
    return current === HAIR ? FACE : HAIR;
  }

  private extractDiscount(variant: CrossSellRegimenExperiment['variant']): void {
    const config = variant?.[this.requiredRegimenClass];
    if (config && typeof config.discount === 'number') {
      this.discountAmount = config.discount;
    }
  }

  private get isPregnantOrNewMom(): boolean {
    const marketingTags: string[] = this.user.get('marketingTags') ?? [];
    return this.PREGNANCY_TAGS.some((tag: string): boolean => marketingTags.includes(tag));
  }

  private get isAcneConcern(): boolean {
    return this.faceMainConcern && this.latestActiveRegimen?.displayConcern?.treatmentConcerns?.length > 0 ?
      this.latestActiveRegimen?.displayConcern?.treatmentConcerns?.includes(this.appConfig.Shared.String.ACNE_OR_PIMPLES)
      : false;
  }

  /** Show only when: female + acne concern + NOT pregnant/breastfeeding/new‑mom */
  get showDandruffBanner(): boolean {
    return this.user.get('Gender') === this.appConfig.Shared.Gender.FEMALE
      && this.isAcneConcern
      && !this.isPregnantOrNewMom;
  }

  /** Show only when: female + (pregnant or breastfeeding or new‑mom) */
  get showHairThinningBanner(): boolean {
    return this.user.get('Gender') === this.appConfig.Shared.Gender.FEMALE
      && this.isPregnantOrNewMom;
  }

  /** Fallback whenever neither of the above two apply */
  get showFallbackBanner(): boolean {
    return !this.showDandruffBanner && !this.showHairThinningBanner;
  }

  onBannerClick(): void {
    const queryParams = { tab: 'regimen', class: this.requiredRegimenClass }
    this.eventLogger.cleverTapEvent('click', JSON.stringify({ name: `${this.requiredRegimenClass.toLowerCase()}_regimen_cross_sell_banner` }));
    this.router.navigate(['/user'], { queryParams });
  }
}
